import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const SubcontractorDiscovery = () => {
  const { instance, accounts } = useMsal();
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const results = await Promise.allSettled([
        authenticatedApiCall(instance, accounts, 'GET', '/subcontractor-discovery/runs'),
        authenticatedApiCall(instance, accounts, 'GET', '/subcontractor-discovery/stats'),
      ]);
      if (results[0].status === 'fulfilled' && results[0].value.data.success) {
        setRuns(results[0].value.data.data || []);
      }
      if (results[1].status === 'fulfilled' && results[1].value.data.success) {
        const raw = results[1].value.data.data || {};
        const files = raw.files || {};
        const lastRun = raw.lastRun || null;
        setStats({
          total_files: parseInt(files.total_files, 10) || 0,
          files_completed: parseInt(files.completed, 10) || 0,
          files_errors: parseInt(files.errors, 10) || 0,
          contractors_found_in_files: parseInt(files.total_contractors_found, 10) || 0,
          llm_discovered_contractors: raw.llmDiscoveredContractors ?? 0,
          last_run_started: lastRun?.started_at || null,
          last_run_status: lastRun?.status || null,
        });
      }
    } catch (err) {
      console.error('Error fetching discovery data:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTrigger = async () => {
    try {
      setTriggering(true);
      setTriggerResult(null);
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/subcontractor-discovery/trigger');
      if (res.data.success) {
        setTriggerResult({ type: 'success', message: res.data.message || 'Crawl triggered successfully!' });
        setTimeout(fetchData, 3000);
      } else {
        setTriggerResult({ type: 'error', message: res.data.message || 'Failed to trigger crawl' });
      }
    } catch (err) {
      console.error('Error triggering crawl:', err);
      setTriggerResult({ type: 'error', message: err.response?.data?.message || 'Failed to trigger crawl' });
    } finally {
      setTriggering(false);
    }
  };

  const cardStyle = {
    backgroundColor: '#fff', borderRadius: 12, padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0',
  };

  const formatDuration = (run) => {
    if (run.duration_seconds) return `${Math.round(run.duration_seconds)}s`;
    if (run.started_at && run.completed_at) {
      const secs = Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000);
      if (secs >= 0) return secs >= 60 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
    }
    return '—';
  };

  const statusBadge = (status) => {
    const colors = {
      completed: { bg: '#dcfce7', text: '#166534' },
      running: { bg: '#dbeafe', text: '#1e40af' },
      failed: { bg: '#fef2f2', text: '#991b1b' },
      pending: { bg: '#fef9c3', text: '#854d0e' },
    };
    const c = colors[status] || colors.pending;
    return (
      <span style={{
        padding: '2px 10px', borderRadius: 12, fontSize: '11px', fontWeight: '600',
        backgroundColor: c.bg, color: c.text, textTransform: 'capitalize',
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: '3px solid #e2e8f0', borderTop: '3px solid #6366f1',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ color: '#64748b', fontSize: '14px' }}>Loading discovery data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
            Subcontractor Discovery
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            SharePoint crawler for automatic subcontractor database enrichment
          </p>
        </div>
        <button
          onClick={handleTrigger}
          disabled={triggering}
          style={{
            padding: '10px 24px', backgroundColor: triggering ? '#94a3b8' : '#6366f1',
            color: '#fff', border: 'none', borderRadius: 8, cursor: triggering ? 'not-allowed' : 'pointer',
            fontSize: '14px', fontWeight: '600', transition: 'all 0.2s',
            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.3)',
          }}
        >
          {triggering ? 'Triggering...' : 'Run Crawl Now'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: '1rem', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {triggerResult && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: '1rem', fontSize: '14px',
          backgroundColor: triggerResult.type === 'success' ? '#dcfce7' : '#fef2f2',
          border: `1px solid ${triggerResult.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: triggerResult.type === 'success' ? '#166534' : '#991b1b',
        }}>
          {triggerResult.message}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ ...cardStyle, borderLeft: '4px solid #6366f1' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', marginBottom: 4 }}>Tracked files</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>{stats.total_files}</div>
          </div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #10b981' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', marginBottom: 4 }}>Extractions OK</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>{stats.files_completed}</div>
          </div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', marginBottom: 4 }}>LLM in DB</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>{stats.llm_discovered_contractors}</div>
          </div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #f59e0b' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', marginBottom: 4 }}>Last run</div>
            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>
              {stats.last_run_started
                ? new Date(stats.last_run_started).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : 'Never'}
              {stats.last_run_status ? (
                <span style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#64748b', marginTop: 4 }}>
                  {stats.last_run_status}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Crawl History */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
          Crawl History
        </h3>
        {runs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
            No crawl runs yet. Click "Run Crawl Now" to start the first one.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Files</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>New Contractors</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Updated</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run, idx) => (
                  <tr key={run.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                      {new Date(run.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                      {statusBadge(run.status)}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: '600' }}>
                      {run.files_processed || 0}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: '600', color: '#10b981' }}>
                      {run.contractors_new ?? run.new_contractors ?? 0}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: '600', color: '#3b82f6' }}>
                      {run.contractors_updated ?? run.updated_contractors ?? 0}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#64748b' }}>
                      {formatDuration(run)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubcontractorDiscovery;
